import AuthLayout from "@/layouts/AuthLayout";
import logoIcon from "@/assets/Logo.png";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useNotification from "@/hooks/use-notification";
import { getUserTypeFromAuthResponse } from "../utils/token.utils";
import { isAdminLoginAllowed } from "@/constants/auth";
import { removeTokens } from "@/utils/auth";
import { setUserFromToken } from "@/store";

import { t } from "i18next";
import StaffLogin from "../components/StaffLogin";
import AdminLogin from "../components/AdminLogin";
import OTPVerification from "../components/OTPVerification";
import RoleSelection from "../components/RoleSelection";
import ForgotPassword from "../components/ForgotPassword";
import CreateNewPassword from "../components/CreateNewPassword";
import StepHeader from "../components/StepHeader";
import styles from "../styles/login.module.scss";
import {
  LoginStep,
  INITIAL_STEP,
  STEPS_WITHOUT_HEADER,
  LoginRole,
} from "../constants/loginSteps";
import { getStepContent } from "../utils/loginSteps.utils";
import { useCompanyInfo } from "../hooks/useCompanyInfo";

const LoginPage = () => {
  const [step, setStep] = useState<LoginStep>(INITIAL_STEP);
  const [selectedRole, setSelectedRole] = useState<LoginRole>(
    LoginRole.EMPLOYEE,
  );
  const [authData, setAuthData] = useState({
    identifier: "",
    domainID: "",
  });
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: "",
    otpCode: "",
  });
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useNotification();
  const { domainID: currentDomainID, domainName: currentDomainName } =
    useCompanyInfo();

  const handleAdminLoginSuccess = (response: any) => {
    const userType = getUserTypeFromAuthResponse(response);
    if (userType === null) {
      removeTokens();
      notifyError(t("login.adminUserTypeError"));
      return;
    }
    if (!isAdminLoginAllowed(userType)) {
      removeTokens();
      notifyError(t("login.adminAccessDenied"));
      return;
    }
    setUserFromToken(response, true);
    notifySuccess(t("login.loginSuccess"));
    navigate("/dashboard");
  };

  const handleLoginFlow = (tokenPayload: any) => {
    const { requiresOTP, identifier, domainID } = tokenPayload;

    if (requiresOTP) {
      setAuthData({ identifier, domainID: domainID || currentDomainID });
      setStep(LoginStep.OTP_VERIFICATION);
    } else {
      navigate("/dashboard");
    }
  };

  const handleForgotPasswordSuccess = (email: string) => {
    setForgotPasswordData((prev) => ({ ...prev, email }));
    setStep(LoginStep.FORGOT_PASSWORD_OTP_2);
  };

  const handleForgotPasswordOtpSuccess = (response: any) => {
    const otpCode = response?.otpCode || response?.otp || response?.code || "";
    setForgotPasswordData((prev) => ({ ...prev, otpCode }));
    setStep(LoginStep.CREATE_NEW_PASSWORD);
  };

  const handleOtpVerification = () => {
    navigate("/dashboard");
  };

  const handleRoleChange = (role: number) => {
    setSelectedRole(role);
    const targetStep =
      role === LoginRole.EMPLOYEE
        ? LoginStep.STAFF_LOGIN
        : LoginStep.ADMIN_LOGIN;
    setStep(targetStep);
  };

  const handlePasswordResetSuccess = () => {
    setStep(LoginStep.ADMIN_LOGIN);
    setSelectedRole(LoginRole.MANAGER);
    setForgotPasswordData({ email: "", otpCode: "" });
  };

  const shouldShowHeader = !STEPS_WITHOUT_HEADER.includes(step);
  const shouldShowRoleSelection =
    step === LoginStep.STAFF_LOGIN || step === LoginStep.ADMIN_LOGIN;
  const stepContent = getStepContent(step) as {
    title: string;
    description: string;
  };

  return (
    <AuthLayout>
      <div className={styles.login}>
        <div className={styles["login__form"]}>
          <div className={styles["login__wrapper"]}>
            <div className={styles["login__logo"]}>
              <img src={logoIcon} alt="BOTAŞ" />
            </div>

            {shouldShowHeader && (
              <StepHeader
                title={stepContent.title}
                description={stepContent.description}
              />
            )}

            {shouldShowRoleSelection && (
              <RoleSelection
                selectedRole={selectedRole}
                onRoleChange={handleRoleChange}
              />
            )}

            {step === LoginStep.ADMIN_LOGIN && (
              <AdminLogin
                currentDomainID={currentDomainID}
                currentDomainName={currentDomainName}
                onForgotPassword={() => setStep(LoginStep.FORGOT_PASSWORD)}
                onLoginSuccess={handleAdminLoginSuccess}
              />
            )}

            {step === LoginStep.STAFF_LOGIN && (
              <StaffLogin
                onLoginSuccess={handleLoginFlow}
                domainID={currentDomainID}
              />
            )}

            {step === LoginStep.OTP_VERIFICATION && (
              <OTPVerification
                onVerificationSuccess={handleOtpVerification}
                onBack={() => setStep(LoginStep.STAFF_LOGIN)}
                identifier={authData.identifier}
                domainID={authData.domainID}
                verificationType="login"
                phoneNumber={authData.identifier}
              />
            )}

            {step === LoginStep.FORGOT_PASSWORD && (
              <ForgotPassword
                onBack={() => setStep(LoginStep.ADMIN_LOGIN)}
                onSuccess={handleForgotPasswordSuccess}
                domainID={currentDomainID}
              />
            )}

            {step === LoginStep.FORGOT_PASSWORD_OTP_2 && (
              <OTPVerification
                onVerificationSuccess={handleForgotPasswordOtpSuccess}
                onBack={() => setStep(LoginStep.FORGOT_PASSWORD)}
                identifier={forgotPasswordData.email}
                domainID={currentDomainID}
                verificationType="forgot-password"
              />
            )}

            {step === LoginStep.CREATE_NEW_PASSWORD && (
              <CreateNewPassword
                onSuccess={handlePasswordResetSuccess}
                email={forgotPasswordData.email}
                otpCode={forgotPasswordData.otpCode}
              />
            )}
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
